import { getDynamicSemanticToken } from '../../../DesignSystem';
import { getDefaultZoomUI } from '../../../ThemeProvider';
export const Dimensions = {
    extraSmall: '20px',
    small: '24px',
    medium: '32px',
    large: '40px',
    extraLarge: '110px',
};
export function getDynamicThemeToken(theme) {
    const semantic = getDynamicSemanticToken(theme);
    const { typography } = theme?.zoomUI ?? getDefaultZoomUI();
    return {
        fontFamily: typography.fontFamily,
        backgroundColor: 'transparent',
        height: Dimensions,
        width: Dimensions,
        fontSize: {
            extraSmall: '10px',
            small: '10px',
            medium: '12px',
            large: '14px',
            extraLarge: '36px',
        },
        fontWeight: {
            extraSmall: typography.fontWeight.label,
            small: typography.fontWeight.label,
            medium: typography.fontWeight.subtitleBold,
            large: typography.fontWeight.bodyBold,
            extraLarge: typography.fontWeight.display1,
        },
        borderRadius: {
            extraSmall: '8px',
            small: '8px',
            medium: '10px',
            large: '14px',
            extraLarge: '38px',
        },
        color: semantic.inverse.global.default,
        background: {
            primary: {
                default: semantic.fill.primary,
                hover: semantic.state.primary.hover,
                active: semantic.state.primary.press,
            },
            secondary: {
                default: semantic.fill.error,
                hover: semantic.state.error.hover,
                active: semantic.state.error.press,
            },
        },
        mask: {
            backgroundColor: {
                hover: semantic.state.hover,
                press: semantic.state.press,
                inactive: semantic.state.inactive,
            },
        },
        status: {
            backgroundColor: semantic.bg.default,
            scale: {
                extraSmall: 1,
                small: 1,
                medium: 1,
                large: 1.5,
                extraLarge: 3,
            },
        },
        outline: {
            focusVisible: `2px solid ${semantic.border.primary}`,
        },
        initialColors: [
            semantic.fill.global.success,
            semantic.fill.global.supplementary1,
            semantic.fill.global.supplementary2,
            semantic.fill.global.supplementary3,
            semantic.fill.global.neutral,
            semantic.fill.global.complementary,
            semantic.fill.global.warning,
            semantic.fill.global.error,
        ],
    };
}
export const transformSizeByNumber = (size) => {
    let prev = 0;
    const dimensions = Object.entries(Dimensions).sort((a, b) => parseInt(a[1], 10) - parseInt(b[1], 10));
    let i = 0;
    for (i = 0; i < dimensions.length; i++) {
        const dimension = dimensions[i];
        if (i > 0) {
            prev = parseInt(dimensions[i - 1][1], 10);
        }
        const value = parseInt(dimension[1], 10);
        // first
        if (i === 0) {
            if (size <= value)
                return dimension[0];
        }
        else {
            if (size > value)
                continue;
            const roundValue = (value + prev) / 2;
            if (size <= roundValue)
                return dimensions[i - 1][0];
            return dimension[0];
        }
    }
    return dimensions[i - 1][0];
};
export const isSize = (size) => typeof size !== 'number';
export const isCustomSize = (size) => !(isSize(size) && size !== 'extraLarge');
export const getSizeNumber = (size) => {
    if (isSize(size))
        return parseInt(Dimensions[size], 10);
    return size;
};
const mediumSizeNum = 32;
const commonFontSizeRate = 12 / mediumSizeNum; // medium.fontSize / medium.size
const commonBorderRadiusRate = 10 / mediumSizeNum; // medium.borderSize / medium.size
export const numberSizeLadders = [
    { size: 24, borderRadius: '8px', fontSize: '10px' }, // small All Prism design
    { size: 32, borderRadius: '10px', fontSize: '12px' }, // medium All Prism design
    { size: 40, borderRadius: '14px', fontSize: '14px' }, // large All Prism design
    { size: 48, borderRadius: '16px', fontSize: `${48 * commonFontSizeRate}px` },
    { size: 64, borderRadius: '22px', fontSize: `${64 * commonFontSizeRate}px` },
    { size: 80, borderRadius: '26px', fontSize: `${80 * commonFontSizeRate}px` },
    { size: 110, borderRadius: '36px', fontSize: '36px' }, // extraLarge All Prism design
];
export function getFontSizeByNumberSize(size) {
    const num = Math.max(Math.floor(size * commonFontSizeRate), 10);
    return `${num}px`;
}
export function getBorderRadiusByNumberSize(size) {
    const num = Math.max(Math.floor(size * commonBorderRadiusRate), 8);
    return `${num}px`;
}
export function getCustomStyleByNumberSize(size) {
    if (!(typeof size === 'number' && size > 0)) {
        // medium default
        return {
            borderRadius: numberSizeLadders[1].borderRadius,
            fontSize: numberSizeLadders[1].fontSize,
        };
    }
    const ladder = numberSizeLadders.find((item) => item.size === size);
    if (ladder) {
        return {
            borderRadius: ladder.borderRadius,
            fontSize: ladder.fontSize,
        };
    }
    return {
        borderRadius: getBorderRadiusByNumberSize(size),
        fontSize: getFontSizeByNumberSize(size),
    };
}
